import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { TabsContent } from '../ui/tabs';
import { Eye, Edit, Trash2, Plus, Video } from 'lucide-react';
import { ListPager, usePagedList } from './ListPager';
import { getCourseColorOption } from '../../lib/courseColors';
import { ListToolbar, useFilteredList } from './ListToolbar';

const CoursesTab = ({
  sortedCourses, handleCourseSort, courseSortConfig, SortIndicatorFor,
  openDialog, deleteCourse, categories, types, t,
}) => {
  const filter = useFilteredList(
    sortedCourses,
    [c => c.name?.ja, 'id', 'registrationNumber'],
    [
      { key: 'category', label: t('admin.courses.category'), options: categories.filter(c => c.id !== 'all').map(c => ({ value: c.id, label: c.name?.ja || c.id })) },
      { key: 'courseType', label: t('admin.courses.courseType'), options: [
        { value: 'in-person', label: t('admin.courses.inPerson') },
        { value: 'on-demand', label: t('admin.courses.onDemand') },
        { value: 'hybrid', label: t('admin.courses.hybrid') },
      ] },
    ]
  );
  const pager = usePagedList(filter.result);
  return (
  <TabsContent value="courses" className="p-4">
    <div className="mb-4">
      <Button onClick={() => openDialog('newCourse')} className="bg-[#00A651] hover:bg-[#008a43]">
        <Plus className="h-4 w-4 mr-2" /> {t('admin.courses.addNew')}
      </Button>
    </div>
    <ListToolbar filter={filter} testId="courses" placeholder="講習名・ID・登録番号で検索" />
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[60px]">{t('admin.common.file')}</TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('name.ja')}>
              <div className="flex items-center gap-1">{t('admin.courses.name')}<SortIndicatorFor config={courseSortConfig} columnKey="name.ja" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('registrationNumber')}>
              <div className="flex items-center gap-1">{t('admin.courses.registrationNumber')}<SortIndicatorFor config={courseSortConfig} columnKey="registrationNumber" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('category')}>
              <div className="flex items-center gap-1">{t('admin.courses.category')}<SortIndicatorFor config={courseSortConfig} columnKey="category" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('type')}>
              <div className="flex items-center gap-1">{t('admin.courses.type')}<SortIndicatorFor config={courseSortConfig} columnKey="type" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('courseType')}>
              <div className="flex items-center gap-1">{t('admin.courses.courseType')}<SortIndicatorFor config={courseSortConfig} columnKey="courseType" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('price')}>
              <div className="flex items-center gap-1">{t('admin.courses.price')}<SortIndicatorFor config={courseSortConfig} columnKey="price" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('days')}>
              <div className="flex items-center gap-1">{t('admin.courses.duration')}<SortIndicatorFor config={courseSortConfig} columnKey="days" /></div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-100 select-none" onClick={() => handleCourseSort('featured')}>
              <div className="flex items-center gap-1">{t('admin.courses.featured')}<SortIndicatorFor config={courseSortConfig} columnKey="featured" /></div>
            </TableHead>
            <TableHead className="w-[120px]">{t('admin.common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pager.total === 0 ? (
            <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">{t('admin.courses.noCourses')}</TableCell></TableRow>
          ) : (
            pager.paged.map((course) => (
              <TableRow key={course.id}>
                <TableCell><img src={course.image} alt={course.name?.ja} className="w-12 h-12 object-cover rounded" /></TableCell>
                <TableCell className="font-medium max-w-[200px]">
                  <div className="truncate flex items-center gap-2">
                    {getCourseColorOption(course.color) && (
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${getCourseColorOption(course.color).bg}`} />
                    )}
                    <span className="truncate">{course.name?.ja}</span>
                  </div>
                  <div className="text-xs text-gray-400">{course.id}</div>
                </TableCell>
                <TableCell className="text-sm text-gray-600">{course.registrationNumber || '-'}</TableCell>
                <TableCell><Badge variant="outline">{categories.find(c => c.id === course.category)?.name?.ja || course.category}</Badge></TableCell>
                <TableCell>
                  <Badge className={course.type === 'skill' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
                    {types.find(t => t.id === course.type)?.name?.ja || course.type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className={course.courseType === 'on-demand' ? 'bg-orange-100 text-orange-800' : course.courseType === 'hybrid' ? 'bg-cyan-100 text-cyan-800' : 'bg-gray-100 text-gray-800'}>
                    {course.courseType === 'on-demand' ? t('admin.courses.onDemand') : course.courseType === 'hybrid' ? t('admin.courses.hybrid') : t('admin.courses.inPerson')}
                  </Badge>
                </TableCell>
                <TableCell>¥{course.price?.toLocaleString()}</TableCell>
                <TableCell>{course.days}日</TableCell>
                <TableCell>{course.featured && <Badge className="bg-yellow-100 text-yellow-800">{t('admin.courses.featured')}</Badge>}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openDialog('editCourse', course)}><Edit className="h-4 w-4" /></Button>
                    {(course.courseType === 'on-demand' || course.courseType === 'hybrid') && (
                      <Button variant="ghost" size="sm" onClick={() => openDialog('manageVideos', course)} title="動画管理"><Video className="h-4 w-4" /></Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteCourse(course.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
    <ListPager pager={pager} testId="courses" />
  </TabsContent>
  );
};

export default CoursesTab;
